import { useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { FiUpload } from 'react-icons/fi';

import {
  Box,
  Button,
  FileButton,
  Flex,
  Group,
  showErrorNotification,
  Text,
} from '@highlight-cp/core-ui';

import { ActionIcons } from '../action-icons';
import { AssetThumbnail } from '../asset-thumbnail';
import { isFileAccepted } from '../ai-textarea/hooks/use-attachments/use-attachments';
import classes from './asset-uploader.module.css';
import {
  DEFAULT_ACCEPTED_FILE_TYPES,
  IMAGE_REJECTED_MESSAGE,
  IMAGE_REJECTED_TITLE,
  MAX_IMAGE_SIZE_BYTES,
  MAX_IMAGE_SIZE_MB,
} from './constants';
import type { AssetUploaderProps } from './types';

const AssetUploader = ({
  accept = DEFAULT_ACCEPTED_FILE_TYPES,
  asset,
  className,
  id,
  isLoading,
  isReadOnly,
  label,
  onChange,
  onDelete,
  onDownload,
  variant = 'outline',
}: AssetUploaderProps) => {
  const { t } = useTranslation();
  const resetRef = useRef<() => void>(null);

  const shouldShowUploadButton = !asset;
  const { name } = asset ?? {};

  const handleChange = (file: File | null) => {
    if (file && (!isFileAccepted(file, accept) || file.size > MAX_IMAGE_SIZE_BYTES)) {
      // The picked file stays on the hidden input, so clear it to allow re-picking the same name.
      resetRef.current?.();
      showErrorNotification({
        title: t(IMAGE_REJECTED_TITLE),
        message: t(IMAGE_REJECTED_MESSAGE, { maxSizeMb: MAX_IMAGE_SIZE_MB }),
      });
      return;
    }

    onChange(file);
  };

  return (
    <>
      {shouldShowUploadButton ? (
        <FileButton onChange={handleChange} accept={accept} resetRef={resetRef}>
          {(props) => (
            <Button
              data-testid="file-input"
              id={id}
              leftSection={<FiUpload />}
              loading={isLoading}
              onClick={() => props.onClick()}
              disabled={isLoading || isReadOnly}
              variant={variant}
              size="xs"
            >
              {label || t('Upload Image')}
            </Button>
          )}
        </FileButton>
      ) : (
        <Group className={clsx(classes.asset, className)} wrap="nowrap" align="start">
          <AssetThumbnail asset={asset} />

          <Box className={classes.fileName}>
            <Text lineClamp={2} title={name ?? undefined}>
              {name ?? ''}
            </Text>
          </Box>
          <Flex align="center" h={50}>
            {onDownload && (
              <ActionIcons.Download
                ariaLabel={t('Download {{name}}', { name })}
                disabled={isLoading}
                isLoading={isLoading}
                onClick={onDownload}
              />
            )}
            <ActionIcons.Delete
              ariaLabel={t('Delete {{name}}', { name })}
              disabled={isReadOnly || isLoading}
              isLoading={isLoading}
              onClick={onDelete}
              confirmationText={t('Are you sure you want to delete {{name}}?', { name })}
              confirmationTitle={t('Delete Image?')}
              confirmationConfirmText={t('Delete Image')}
            />
          </Flex>
        </Group>
      )}
    </>
  );
};

export default AssetUploader;
